import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import moment from 'moment';
import momentTz from 'moment-timezone';

import {
  DillTable,
  DillButton,
  DillDropDownFilter,
  DillDropdown,
  DillCheckbox,
  type DillSummaryCard,
  type DillColumn,
} from '../../../general/Dill';

import {
  getBuyerTransactions,
  getMainBuyerPaymentMethods,
  getTransactionById,
  deletePaymentMethodByMainBuyer,
} from '../../../../redux/services/paymentsService';
import { getGroupedBuyersSuppliers } from '../../../../redux/services/buyersService';
import { updateSelectedSupplierForBuyer } from '../../../../redux/globalSlices/invoiceSlice';
import { closeMessageModal, openMessageModal } from '../../../../redux/globalSlices/genericSlice';

import { AppPaidInvoicesModal } from '../../../general/AppPaidInvoicesModal/AppPaidInvoicesModal';
import { AppExternalPaymentDetailsModal } from '../../../general/AppExternalPaymentDetailsModal/AppExternalPaymentDetailsModal';
import AppDateRangePickerModal from '../../../general/AppDateRangePickerModal/AppDateRangePickerModal';
import AppAddPaymentMethodModal from '../../../general/AppAddPaymentMethodModal/AppAddPaymentMethodModal';
import AppTransactionStatusPill from '../../../general/AppTransactionStatusPill/AppTransactionStatusPill';

import {
  Bank,
  Buildings,
  CreditCard,
  Coins,
  Warning,
  Calendar,
  CurrencyCircleDollar,
  ArrowClockwise,
  CaretRight,
  Receipt,
  Money,
  PaperPlaneRight,
  Storefront,
  X,
} from 'phosphor-react';
import { ArrowDownCircle } from 'react-feather';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { ClockCountdown } from '@phosphor-icons/react';
import { AppAutopayModal } from '../../../general/AppAutopayModal/AppAutopayModal';
import { BuyerScheduledPayments } from './BuyerScheduledPayments';

// Mirrors SupplierPaymentTable's PAYMENT_TYPE_META - the buyer-side subset of
// DillTransactionTypesSchema that gets a type icon + tooltip on its row.
const PAYMENT_TYPE_META: Partial<Record<string, { icon: typeof Receipt; label: string }>> = {
  PAYMENT: { icon: Receipt, label: 'Invoice Payment' },
  FIXED_AMOUNT_PAYMENT: { icon: Money, label: 'Fixed Amount Payment' },
  PAYMENT_REQUEST_PAYMENT: { icon: PaperPlaneRight, label: 'Payment Request Payment' },
  WILL_CALL_PAYMENT: { icon: Storefront, label: 'Virtual POS Payment' },
};

import {
  DillPaymentMethod,
  DillTransaction,
  DillTransactionStatus,
  formatMoney,
  SupplierForBuyer,
  appDelay,
} from '@dill/dill-shared';
import { COLORS } from '../../../../utils/colors';
import { BuyerPaymentMethods } from '../BuyerPaymentMethods/BuyerPaymentMethods';

interface PaymentRow {
  id: string;
  invoices: string;
  amount: string;
  createdAt: string;
  createdAtSort: number;
  paidTo: string;
  transactionStatus: DillTransactionStatus | undefined;
  method: string;
  methodType: string;
  cardType?: string;
  paymentMethodName?: string;
  type: string;
  invoiceNumbers?: string[];
  // Paid outside of Dill (e.g. a check recorded in the supplier's ERP)
  isExternalPayment: boolean;
  externalReferenceNumber?: string;
}

export const BuyerPayments = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  useParams();
  const { user } = useAppSelector((state) => state.auth);
  const [selectedMainBuyerId] = useParamMainBuyerId();

  const { buyerTransactions, paymentMethods } = useAppSelector((state) => state.payments);
  const { selectedBuyerSuppliers, selectedMainBuyer } = useAppSelector((state) => state.buyers);
  const { selectedSupplierForBuyer } = useAppSelector((state) => state.invoice);
  const { isPreviewMode } = useAppSelector((state) => state.preview);

  const [isMobileWidth, setIsMobileWidth] = useState(() => window.innerWidth < 800);
  useEffect(() => {
    const onResize = () => setIsMobileWidth(window.innerWidth < 800);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [dateRange, setDateRange] = useState<{ startDate?: Date; endDate?: Date }>({
    startDate: moment(new Date()).subtract(3, 'months').toDate(),
    endDate: new Date(),
  });
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const validTimezone = momentTz.tz.zone(timezone) ? timezone : 'America/Los_Angeles';

  const [isAppDateRangePickerModalOpen, setIsAppDateRangePickerModalOpen] = useState(false);
  const [isAppAddPaymentMethodModalOpen, setIsAppAddPaymentMethodModalOpen] = useState(false);
  const [openPaidInvoiceDetails, setOpenPaidInvoiceDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<DillTransaction | null>(null);
  const [selectedExternalPayment, setSelectedExternalPayment] = useState<DillTransaction | null>(
    null
  );
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] } | null>(null);
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const handleShowPaidInvoicesRef = useRef<((payment: DillTransaction) => void) | null>(null);
  const [openAutopayModal, setOpenAutopayModal] = useState(false);

  useEffect(() => {
    const mainRoles = user?.roles.filter((role) => role !== 'DILL_ADMIN');
    if ((mainRoles?.includes('BUYER') || isPreviewMode) && selectedMainBuyerId) {
      dispatch(
        getBuyerTransactions({
          mainBuyerId: selectedMainBuyerId,
          startDate: dateRange?.startDate
            ? moment(dateRange?.startDate).format('MMM DD, YYYY')
            : '',
          endDate: dateRange?.endDate ? moment(dateRange?.endDate).format('MMM DD, YYYY') : '',
          timezone: validTimezone,
        })
      );
      dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId }));
    }
  }, [selectedMainBuyerId, location.pathname, user?.roles]);

  useEffect(() => {
    if (selectedMainBuyerId && user?.userMainBuyers && !isPreviewMode) {
      const mainBuyer = user?.userMainBuyers
        .filter((mb) => !mb?.isDeactivated && !mb?.isCODCustomer)
        .find((mb) => mb.id === selectedMainBuyerId);
      if (!mainBuyer) {
        navigate('/companies');
      }
    }
  }, [user?.userMainBuyers, isPreviewMode]);

  useEffect(() => {
    if (dateRange && selectedMainBuyerId) {
      dispatch(
        getBuyerTransactions({
          mainBuyerId: selectedMainBuyerId || '',
          startDate: dateRange?.startDate
            ? moment(dateRange?.startDate).format('MMM DD, YYYY')
            : '',
          endDate: dateRange?.endDate ? moment(dateRange?.endDate).format('MMM DD, YYYY') : '',
          timezone: validTimezone,
        })
      );
    }
    return () => {};
  }, [dateRange]);

  useEffect(() => {
    if (location.pathname.startsWith(`/companies/${selectedMainBuyerId}/payments`)) {
      const searchParams = new URLSearchParams(location.search);
      const transactionId = searchParams.get('transactionId');
      if (transactionId) {
        const transaction = buyerTransactions.find((trans) => trans.id === transactionId);
        if (transaction && !transaction.isExternalPayment) {
          handleShowPaidInvoicesRef.current?.(transaction);
        }
      }
    }
  }, [location.pathname, buyerTransactions, selectedMainBuyerId]);

  useEffect(() => {
    if (selectedMainBuyerId) {
      dispatch(getGroupedBuyersSuppliers({ mainBuyerId: selectedMainBuyerId, weeksLimit: 2 }));
    }
  }, [selectedMainBuyerId]);

  useEffect(() => {
    let newSupplier: SupplierForBuyer | null = null;
    if (selectedBuyerSuppliers.length > 0 && location.pathname.includes('/payments')) {
      const supplier: SupplierForBuyer = selectedBuyerSuppliers[0];
      newSupplier = supplier ?? null;
      dispatch(updateSelectedSupplierForBuyer(newSupplier));
    }
  }, [selectedMainBuyerId, selectedBuyerSuppliers]);

  const buyerForMainBuyer = useMemo(() => {
    return user?.userBuyers?.find(
      (buy) =>
        buy.supplierId === selectedSupplierForBuyer?.id &&
        selectedMainBuyer &&
        selectedMainBuyer.quickBooksCustomerId === buy.quickbooksCustomerId
    );
  }, [selectedSupplierForBuyer, selectedMainBuyer, user?.userBuyers]);

  const handleShowPaidInvoices = useCallback(
    async (payment: DillTransaction) => {
      const transactionResults = await dispatch(getTransactionById({ transactionId: payment.id }));
      let finalTransaction = payment;
      if (transactionResults.type === 'payments/getTransactionById/fulfilled') {
        const res = transactionResults?.payload as any;
        finalTransaction = res.data as DillTransaction;
      }
      setSelectedTransaction(finalTransaction);
      setOpenPaidInvoiceDetails(true);
    },
    [dispatch]
  );
  handleShowPaidInvoicesRef.current = handleShowPaidInvoices;

  const handleViewMainPayment = (val: string) => {
    const transaction = buyerTransactions.find((trans) => trans.id === val);
    if (transaction) {
      navigate(`/companies/${selectedMainBuyerId}/payments?transactionId=${val}`);
      handleShowPaidInvoices(transaction);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethod: DillPaymentMethod) => {
    const isBankAccount = paymentMethod.type === 'BANK_ACCOUNT';
    const warningMessage = isBankAccount
      ? 'This action cannot be undone. If you delete a bank method and decide to re-add the same one, you will need to wait 48 hours.'
      : 'This action can not be undone.';

    dispatch(
      openMessageModal({
        buttonText: 'Cancel',
        isSecondButton: true,
        secondButtonText: 'Delete',
        modalType: 'WARN',
        title: 'Are you sure you want to delete this payment method?',
        subTitle: warningMessage,
        onSecondButtonClick: async () => {
          await appDelay(10);
          dispatch(
            openMessageModal({
              buttonText: '',
              modalType: 'LOADING',
              title: 'Deleting',
              subTitle: 'Deleting payment method',
            })
          );
          const deleteResults = await dispatch(
            deletePaymentMethodByMainBuyer({
              mainBuyerId: selectedMainBuyerId || '',
              paymentMethodId: paymentMethod.id,
            })
          );
          if (deleteResults.type === 'payments/deletePaymentMethod/rejected') {
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'ERROR',
                title: 'Error deleting payment method',
                subTitle: `${
                  deleteResults.payload as any
                } Contact (628) 304-0931 for customer support`,
              })
            );
          } else if (deleteResults.type === 'payments/deletePaymentMethod/fulfilled') {
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'SUCCESS',
                title: 'Payment method deleted',
                subTitle: 'Payment method deleted successfully',
                onClose: () => {
                  dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
                },
              })
            );
          } else {
            closeMessageModal();
          }
        },
      })
    );
  };

  const isAutoPaySelected = useMemo(() => {
    return selectedSupplierForBuyer?.buyer?.isAutopayActive ?? false;
  }, [selectedSupplierForBuyer]);

  const isAutopaySetupEnabled = useMemo(() => {
    return selectedSupplierForBuyer?.isAutoPayEnabled ?? false;
  }, [selectedSupplierForBuyer]);

  const isAutoPayActive = useMemo(() => {
    return isAutoPaySelected && isAutopaySetupEnabled;
  }, [isAutoPaySelected, isAutopaySetupEnabled]);

  const filteredPaymentMethods = useMemo(() => {
    return paymentMethods.filter((pm) => {
      if (isPreviewMode) return false;
      if (pm.provider === 'DWOLLA' && buyerForMainBuyer?.disabledPaymentTypes?.includes('BANK'))
        return false;
      if (
        pm.cardType === 'CREDIT' &&
        buyerForMainBuyer?.disabledPaymentTypes?.includes('CREDIT_CARD')
      )
        return false;
      if (
        pm.cardType &&
        pm.cardType !== 'CREDIT' &&
        buyerForMainBuyer?.disabledPaymentTypes?.includes('DEBIT_CARD')
      )
        return false;
      if (pm.type === 'CREDIT_CARD' && selectedSupplierForBuyer?.isOptoutCardPayments) return false;
      if (pm.cardType && pm.cardType !== 'CREDIT' && selectedSupplierForBuyer?.isDebitCardDisabled)
        return false;
      if (pm.provider === 'DWOLLA') return true;
      if (
        pm.provider === 'STRIPE' &&
        (selectedSupplierForBuyer?.cardPaymentProvider === 'STRIPE' ||
          !selectedSupplierForBuyer?.cardPaymentProvider)
      )
        return true;
      if (pm.provider === 'FISERV' && selectedSupplierForBuyer?.cardPaymentProvider === 'FISERV')
        return true;
      return false;
    });
  }, [paymentMethods, isPreviewMode, buyerForMainBuyer, selectedSupplierForBuyer]);

  const hasExternalPayments = useMemo(
    () => buyerTransactions.some((transaction) => transaction.isExternalPayment),
    [buyerTransactions]
  );

  const buyerTransactionsFiltered = useMemo(() => {
    let finalFiltered = buyerTransactions;

    const selectedPaymentMethods = selectedFilters?.status ?? [];
    const selectedStatuses = selectedFilters?.transactionStatus ?? [];
    const selectedSources = selectedFilters?.source ?? [];
    const jobIds = selectedJobIds.filter((id) => id !== 'ALL');
    const hasActiveFilters =
      selectedPaymentMethods.length > 0 ||
      selectedStatuses.length > 0 ||
      selectedSources.length > 0 ||
      jobIds.length > 0;

    if (buyerTransactions.length > 0 && hasActiveFilters) {
      finalFiltered = buyerTransactions.filter((transaction) => {
        const filters: {
          isMatchPaymentMethodTypeFilter?: boolean;
          isMatchStatusFilter?: boolean;
          isMatchJobFilter?: boolean;
          isMatchSourceFilter?: boolean;
        } = {};

        if (selectedSources.length > 0) {
          filters.isMatchSourceFilter = selectedSources.includes(
            transaction.isExternalPayment ? 'EXTERNAL' : 'DILL'
          );
        }

        if (selectedPaymentMethods.length > 0) {
          filters.isMatchPaymentMethodTypeFilter =
            (transaction?.paymentType === 'BANK' &&
              selectedPaymentMethods.includes('BANK_ACH_ONLY')) ||
            (transaction?.paymentType === 'CARD' && selectedPaymentMethods.includes('CARD_ONLY'));
        }

        if (selectedStatuses.length > 0) {
          filters.isMatchStatusFilter = selectedStatuses.includes(
            transaction.transactionStatus ?? ''
          );
        }

        if (jobIds.length > 0) {
          filters.isMatchJobFilter = transaction.invoicePayments.some(
            (inv) => inv.job && jobIds.includes(inv.job.id)
          );
        }

        return Object.values(filters).every((item) => item);
      });
    }

    return finalFiltered;
  }, [buyerTransactions, selectedFilters, selectedJobIds]);

  const jobsMap: { [id: string]: { label: string; value: string } } = {};
  buyerTransactions.forEach((transaction) => {
    transaction.invoicePayments.forEach((inv) => {
      if (inv.job && !jobsMap[inv.job.id]) {
        jobsMap[inv.job.id] = {
          label: inv.job.jobNumber ? `${inv.job.name} - ${inv.job.jobNumber}` : inv.job.name,
          value: inv.job.id,
        };
      }
    });
  });
  const jobsFilterOptions = Object.values(jobsMap);

  const activeJobIds = selectedJobIds.filter((id) => id !== 'ALL');
  const activeJobs = activeJobIds.map((id) => jobsMap[id]).filter(Boolean) as {
    label: string;
    value: string;
  }[];

  const paymentRows: PaymentRow[] = useMemo(() => {
    return buyerTransactionsFiltered.map((transaction) => {
      const invoices = transaction.invoicePayments;
      let invoicesValue = '';
      if (transaction.type === 'FIXED_AMOUNT_PAYMENT') {
        invoicesValue = transaction?.partialPaymentNotes || '-';
      } else if (invoices.length > 1) {
        invoicesValue = `${invoices[0].invoiceNumber}... ${
          invoices[invoices.length - 1].invoiceNumber
        }`;
      } else if (invoices.length === 1) {
        invoicesValue = invoices[0].invoiceNumber;
      } else {
        invoicesValue = '-';
      }

      const methodType =
        transaction.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
        transaction.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
          ? 'FEES'
          : transaction.type;

      return {
        id: transaction.id,
        invoices: invoicesValue,
        amount: formatMoney(transaction.totalAmountPaid ?? 0, 2),
        createdAt: moment(transaction.createdAt).format('MM/DD/YYYY'),
        createdAtSort: transaction.createdAt ? new Date(transaction.createdAt).getTime() : 0,
        paidTo: transaction?.recipient?.name ?? '',
        transactionStatus: transaction.transactionStatus,
        method: '',
        methodType,
        cardType: transaction.cardType ?? undefined,
        paymentMethodName: transaction.paymentMethodName ?? undefined,
        type: transaction.type,
        invoiceNumbers: invoices.map((inv) => inv.invoiceNumber),
        isExternalPayment: !!transaction.isExternalPayment,
        externalReferenceNumber: transaction.externalReferenceNumber ?? undefined,
      };
    });
  }, [buyerTransactionsFiltered]);

  const handleShowDetails = useCallback(
    (row: PaymentRow) => {
      const transaction = buyerTransactionsFiltered.find((t) => t.id === row.id);
      if (!transaction) return;
      if (transaction.isExternalPayment) {
        setSelectedExternalPayment(transaction);
        return;
      }
      handleShowPaidInvoices(transaction);
      if (location.pathname.startsWith(`/companies/${selectedMainBuyerId}/payments`)) {
        navigate(`/companies/${selectedMainBuyerId}/payments?transactionId=${row.id}`);
      }
    },
    [
      buyerTransactionsFiltered,
      handleShowPaidInvoices,
      location.pathname,
      navigate,
      selectedMainBuyerId,
    ]
  );

  // Only shown when the buyer has payments made outside of Dill, so the flag is meaningful.
  const renderSourcePill = (row: PaymentRow) =>
    !hasExternalPayments ? null : row.isExternalPayment ? (
      <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold leading-none whitespace-nowrap">
        Outside Dill
      </span>
    ) : (
      <span className="ml-1.5 px-1.5 py-0.5 rounded-full PRIMARY_100-BG PRIMARY_500-CLR text-[10px] font-semibold leading-none whitespace-nowrap">
        Paid on Dill
      </span>
    );

  const columns: DillColumn<PaymentRow>[] = useMemo(
    () => [
      {
        key: 'invoices',
        label: 'Invoice #',
        width: 180,
        sortable: false,
        getSearchValue: (row: PaymentRow) =>
          [row.invoices, row.paidTo, ...(row.invoiceNumbers ?? [])].join(' '),
        render: (_: any, row: PaymentRow) => (
          <div className="w-full flex items-center pr-1">
            <div className="mx-0.5">
              <div className="aspect-square rounded-full SUCCESS_100-BG p-2">
                <ArrowDownCircle color={COLORS.SUCCESS_500} size={16} />
              </div>
            </div>
            <div className="ml-1">
              <div className="text-xs font-semibold">{row.invoices}</div>
              <div className="flex items-center mt-0.5">
                <div className="text-2xs text-gray-500">PAYMENT</div>
                {renderSourcePill(row)}
                {(() => {
                  const paymentTypeMeta = PAYMENT_TYPE_META[row.type];
                  if (!paymentTypeMeta) return null;
                  const { icon: PaymentTypeIcon, label: paymentTypeLabel } = paymentTypeMeta;
                  return (
                    <>
                      <span
                        className="ml-1.5 flex items-center"
                        data-tooltip-id={`payment-type-${row.id}`}>
                        <PaymentTypeIcon size={12} color={COLORS.PRIMARY_500} />
                      </span>
                      <ReactTooltip
                        id={`payment-type-${row.id}`}
                        place="top"
                        variant="dark"
                        opacity="100%"
                        style={{ borderRadius: '8px', fontSize: '12px', zIndex: 10000 }}>
                        {paymentTypeLabel}
                      </ReactTooltip>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: 'amount',
        label: 'Amount',
        width: 100,
        sortable: true,
        render: (_: any, row: PaymentRow) => (
          <p className="text-xs text-left w-full">{row.amount}</p>
        ),
      },
      {
        key: 'createdAt',
        label: 'Date',
        width: 100,
        sortable: true,
        defaultSortDir: 'desc',
        getSortValue: (row: PaymentRow) => row.createdAtSort,
      },
      {
        key: 'paidTo',
        label: 'Paid To',
        width: 160,
        sortable: true,
        render: (_: any, row: PaymentRow) => (
          <p className="text-xs text-left w-full capitalize">{row.paidTo.toLowerCase()}</p>
        ),
      },
      {
        key: 'transactionStatus',
        label: 'Status',
        width: 140,
        render: (_: any, row: PaymentRow) => (
          <div className="w-full mr-6">
            <AppTransactionStatusPill
              status={row.transactionStatus ?? ('pending' as DillTransactionStatus)}
            />
          </div>
        ),
      },
      {
        key: 'method',
        label: 'Method',
        width: 170,
        sortable: false,
        render: (_: any, row: PaymentRow) => {
          if (row.isExternalPayment) {
            return (
              <div className="w-full flex">
                <div className="flex justify-center items-center min-w-[32px] h-8 bg-slate-100 rounded self-center mt-1 mr-3">
                  <Money size={20} style={{ display: 'flex', alignSelf: 'center' }} />
                </div>
                <div>
                  <div className="text-xs font-bold">Paid to supplier directly</div>
                  {row.externalReferenceNumber && (
                    <div className="text-xs text-gray-500">Ref #{row.externalReferenceNumber}</div>
                  )}
                </div>
              </div>
            );
          }
          const transaction = buyerTransactionsFiltered.find((t) => t.id === row.id);
          if (!transaction || row.methodType === 'FEES') return null;
          return (
            <div className="w-full flex">
              <div className="flex justify-center items-center min-w-[32px] h-8 bg-slate-100 rounded self-center mt-1 mr-3">
                {transaction?.paymentMethod?.type === 'BANK_ACCOUNT' ? (
                  <Bank size={20} style={{ display: 'flex', alignSelf: 'center' }} />
                ) : transaction.type === 'CREDIT_PAYMENT' ? (
                  <Coins size={20} style={{ display: 'flex', alignSelf: 'center' }} />
                ) : (
                  <CreditCard size={20} style={{ display: 'flex', alignSelf: 'center' }} />
                )}
              </div>
              <div>
                {transaction?.paymentMethod?.type === 'BANK_ACCOUNT' ? (
                  <div className="text-xs font-bold">Bank Transfer</div>
                ) : transaction.type === 'CREDIT_PAYMENT' ? (
                  <div className="text-xs font-bold">Credits</div>
                ) : transaction?.cardType ? (
                  <div className="text-xs font-bold">
                    {transaction?.cardType === 'CREDIT' ? 'Credit Card' : 'Debit / Other Card'}
                  </div>
                ) : transaction?.paymentMethod?.cardType ? (
                  <div className="text-xs font-bold">
                    {transaction?.paymentMethod?.cardType === 'CREDIT'
                      ? 'Credit Card'
                      : 'Debit / Other Card'}
                  </div>
                ) : (
                  <div className="text-xs font-bold">Credit/Debit Card</div>
                )}
                <div className="text-xs text-gray-500">
                  {transaction?.paymentMethodName ??
                    transaction?.paymentMethod?.paymentMethodName ??
                    ''}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        key: 'viewDetails',
        label: '',
        width: 80,
        sortable: false,
        render: (_: any, row: PaymentRow) => (
          <div className="flex ml-1">
            <DillButton
              label="Details"
              variant="secondary"
              onClick={() => handleShowDetails(row)}
            />
          </div>
        ),
      },
    ],
    [buyerTransactionsFiltered, handleShowDetails, hasExternalPayments]
  );

  const createCheckboxItem = ({
    label,
    value,
    isChecked,
  }: {
    label: string;
    value: string;
    isChecked: boolean;
  }) => ({
    label,
    value,
    labelInElement: (
      <div className="flex mx-1 my-1 items-center gap-2">
        <DillCheckbox isChecked={isChecked} />
        <div className="text-black text-xs">{label}</div>
      </div>
    ),
  });

  const paymentMethodTypesFilterOptions = [
    { label: 'Card Only', value: 'CARD_ONLY' },
    { label: 'Bank ACH Only', value: 'BANK_ACH_ONLY' },
  ];

  const transactionStatusFilterOptions = [
    { label: 'Pending', value: 'pending' },
    { label: 'Processed', value: 'processed' },
    { label: 'Failed', value: 'failed' },
  ];

  const sourceFilterOptions = [
    { label: 'Paid on Dill', value: 'DILL' },
    { label: 'Paid outside Dill', value: 'EXTERNAL' },
  ];

  const filterItems = [
    ...(hasExternalPayments
      ? [
          {
            id: 'source',
            label: 'Source',
            value: sourceFilterOptions.map((option) =>
              createCheckboxItem({
                label: option.label,
                value: option.value,
                isChecked: selectedFilters?.source?.includes(option.value) ?? false,
              })
            ),
          },
        ]
      : []),
    {
      id: 'status',
      label: 'Payment methods',
      value: paymentMethodTypesFilterOptions.map((option) =>
        createCheckboxItem({
          label: option.label,
          value: option.value,
          isChecked: selectedFilters?.status?.includes(option.value) ?? false,
        })
      ),
    },
    {
      id: 'transactionStatus',
      label: 'Status',
      value: transactionStatusFilterOptions.map((option) =>
        createCheckboxItem({
          label: option.label,
          value: option.value,
          isChecked: selectedFilters?.transactionStatus?.includes(option.value) ?? false,
        })
      ),
    },
  ];

  const handleFilterChange = (data: Record<string, { value: string }[]>) => {
    const transformed = Object.fromEntries(
      Object.entries(data).map(([key, arr]) => [key, arr.map((item) => item.value)])
    );
    setSelectedFilters(transformed);
  };

  const toolbarLeft = (
    <div className="flex items-center gap-2">
      <div className="flex flex-row items-center">
        <button
          onClick={() => setIsAppDateRangePickerModalOpen(true)}
          className="flex flex-row items-center GREY_200-BORDER border rounded-lg py-2 px-2 WHITE-BG hover:bg-slate-200">
          <Calendar className="text-slate-500 mr-2" size={18} />
          <div className="text-xs TEXT_PRIMARY-CLR">{`${
            dateRange?.startDate ? moment(dateRange?.startDate).format('MMM DD, YYYY') : ''
          }-${dateRange?.endDate ? moment(dateRange?.endDate).format('MMM DD, YYYY') : ''}`}</div>
        </button>
      </div>
      <div className="w-[220px]">
        <DillDropDownFilter
          placeholder="Filters"
          closeMenu={false}
          isMulti
          showSelectedItems
          multiValue={selectedFilters}
          items={filterItems}
          showMinimalDesign
          onMultiSelectChange={handleFilterChange}
          inputHeight={33}
        />
      </div>
    </div>
  );

  const sharedModals = (
    <>
      <AppPaidInvoicesModal
        open={openPaidInvoiceDetails}
        handleClose={() => {
          setOpenPaidInvoiceDetails(false);
          if (location.pathname.startsWith('/companies')) {
            navigate(`/companies/${selectedMainBuyerId}/payments`);
          }
        }}
        accountType="buyer"
        selectedTransaction={selectedTransaction}
        onViewMainPayment={handleViewMainPayment}
      />

      <AppExternalPaymentDetailsModal
        transaction={selectedExternalPayment}
        accountType="buyer"
        onClose={() => setSelectedExternalPayment(null)}
      />

      <AppDateRangePickerModal
        open={isAppDateRangePickerModalOpen}
        selectedDateRange={dateRange}
        handleClose={() => setIsAppDateRangePickerModalOpen(false)}
        onSelectDateRange={(selection) => {
          setIsAppDateRangePickerModalOpen(false);
          setDateRange(selection);
        }}
      />

      <AppAddPaymentMethodModal
        open={isAppAddPaymentMethodModalOpen}
        handleClose={() => {
          dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
          setIsAppAddPaymentMethodModalOpen(false);
        }}
        isDebitCardDisabled={
          selectedSupplierForBuyer?.isDebitCardDisabled ||
          buyerForMainBuyer?.disabledPaymentTypes?.includes('DEBIT_CARD')
            ? true
            : false
        }
        isCreditCardDisabled={
          buyerForMainBuyer?.disabledPaymentTypes?.includes('CREDIT_CARD') ? true : false
        }
        isBankDisabled={buyerForMainBuyer?.disabledPaymentTypes?.includes('BANK') ? true : false}
        paymentOptionsToShow={
          selectedSupplierForBuyer?.isOptoutCardPayments
            ? 'BANK'
            : selectedSupplierForBuyer?.cardPaymentProvider === 'FISERV'
            ? 'BANK_AND_FISERV_CARD'
            : 'ALL'
        }
        userType="buyer"
      />

      <AppAutopayModal
        open={openAutopayModal}
        handleClose={() => setOpenAutopayModal(false)}
        supplierName={selectedSupplierForBuyer?.name ?? ''}
        buyerName={selectedMainBuyer?.name.toLowerCase() ?? ''}
        isAutoPaySelected={isAutoPaySelected}
        buyer={selectedSupplierForBuyer?.buyer ?? null}
        onAddNewPayment={() => setIsAppAddPaymentMethodModalOpen(true)}
      />
    </>
  );

  const renderMobileCard = useCallback(
    (row: PaymentRow) => {
      const transaction = buyerTransactionsFiltered.find((t) => t.id === row.id);
      const invoiceCount = row.invoiceNumbers?.length ?? 0;

      const methodIcon = row.isExternalPayment ? (
        <Money size={14} color="#6B7280" />
      ) : transaction?.paymentMethod?.type === 'BANK_ACCOUNT' ? (
        <Bank size={14} color="#6B7280" />
      ) : row.type === 'CREDIT_PAYMENT' ? (
        <Coins size={14} color="#6B7280" />
      ) : (
        <CreditCard size={14} color="#6B7280" />
      );
      const methodLabel = row.isExternalPayment
        ? 'Paid to supplier directly'
        : transaction?.paymentMethod?.type === 'BANK_ACCOUNT'
        ? 'Bank Transfer'
        : row.type === 'CREDIT_PAYMENT'
        ? 'Credits'
        : row.cardType === 'CREDIT'
        ? 'Credit Card'
        : row.cardType === 'DEBIT'
        ? 'Debit Card'
        : 'Credit/Debit Card';

      return (
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[13px] font-medium text-gray-900 truncate">
                {row.invoices !== '-' ? `# ${row.invoices}` : 'Payment'}
              </span>
              {invoiceCount > 1 && (
                <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium leading-none flex-shrink-0">
                  {invoiceCount} invoices
                </span>
              )}
              {renderSourcePill(row)}
            </div>
            <AppTransactionStatusPill
              status={row.transactionStatus ?? ('pending' as DillTransactionStatus)}
            />
          </div>

          {/* Amount · Date · Paid To */}
          <div className="grid grid-cols-3 gap-2 mb-2">
            <div>
              <div className="text-[11px] text-gray-400 mb-0.5">Amount</div>
              <div className="text-[12px] font-medium text-gray-900">{row.amount}</div>
            </div>
            <div>
              <div className="text-[11px] text-gray-400 mb-0.5">Date</div>
              <div className="text-[12px] text-gray-900">{row.createdAt}</div>
            </div>
            <div>
              <div className="text-[11px] text-gray-400 mb-0.5">Paid To</div>
              <div className="text-[12px] text-gray-900 capitalize truncate">
                {row.paidTo ? row.paidTo.toLowerCase() : 'N/A'}
              </div>
            </div>
          </div>

          {/* Payment method + action */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            {row.methodType !== 'FEES' ? (
              <div className="flex items-center gap-1.5 text-[12px] text-gray-400">
                {methodIcon}
                <span>{methodLabel}</span>
                {row.paymentMethodName && (
                  <span className="text-gray-400">· {row.paymentMethodName}</span>
                )}
                {row.externalReferenceNumber && (
                  <span className="text-gray-400">· Ref #{row.externalReferenceNumber}</span>
                )}
              </div>
            ) : (
              <div />
            )}
            <DillButton
              label="View Details"
              variant="primary"
              onClick={() => handleShowDetails(row)}
            />
          </div>
        </div>
      );
    },
    [buyerTransactionsFiltered, handleShowDetails, hasExternalPayments]
  );

  return (
    <div className="w-full h-full p-4 overflow-hidden flex flex-col gap-4">
      {jobsFilterOptions.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          <div className="relative z-20">
            <DillDropdown
              inputFieldStyles={{ width: '160px', height: '36px', padding: '2px 2px' }}
              placeholder="Jobs"
              isSearchable={false}
              isCreatable={false}
              closeMenu={false}
              customViewElement={
                <div className="flex my-1 px-2 items-center gap-2">
                  <Buildings size={16} className="text-blue-500" />
                  <p className="text-sm text-gray-600">Jobs Filter</p>
                  {activeJobIds.length > 0 && (
                    <span className="bg-blue-500 text-white rounded-full text-xs font-bold h-5 w-5 flex items-center justify-center ring-2 ring-white shadow-md flex-shrink-0">
                      {activeJobIds.length}
                    </span>
                  )}
                </div>
              }
              itemWidth={300}
              items={[{ label: 'All Jobs', value: 'ALL' }, ...jobsFilterOptions].map((opt) => ({
                ...opt,
                labelInElement: (
                  <div className="flex m-1 items-center gap-1">
                    <DillCheckbox isChecked={selectedJobIds.includes(opt.value)} />
                    <Buildings size={16} className="text-blue-500 flex-shrink-0" />
                    <div className="text-black text-xs truncate">{opt.label}</div>
                  </div>
                ),
              }))}
              isMulti
              onMultiSelectChange={(data, oldMultiValue) => {
                if (data) {
                  const valueList = data.map((item: any) => item.value);
                  const valueListWithoutAll = valueList.filter((item: string) => item !== 'ALL');
                  if (
                    !valueList.includes('ALL') &&
                    oldMultiValue &&
                    oldMultiValue.includes('ALL') &&
                    valueListWithoutAll.length === jobsFilterOptions.length
                  ) {
                    setSelectedJobIds([]);
                  } else if (
                    valueList.includes('ALL') &&
                    valueListWithoutAll.length < jobsFilterOptions.length &&
                    oldMultiValue &&
                    oldMultiValue.includes('ALL')
                  ) {
                    setSelectedJobIds(valueListWithoutAll);
                  } else if (
                    (valueList.includes('ALL') &&
                      valueListWithoutAll.length === 0 &&
                      oldMultiValue &&
                      !oldMultiValue.includes('ALL')) ||
                    valueList.includes('ALL') ||
                    (valueListWithoutAll.length === jobsFilterOptions.length &&
                      !valueList.includes('ALL'))
                  ) {
                    setSelectedJobIds(['ALL', ...jobsFilterOptions.map((j) => j.value)]);
                  } else if (valueList.length > 0) {
                    setSelectedJobIds(valueList);
                  } else {
                    setSelectedJobIds([]);
                  }
                }
              }}
              value={null}
              multiValue={selectedJobIds}
              menuAlign="left"
            />
          </div>
          {activeJobIds.length > 0 && (
            <>
              {activeJobs.slice(0, 2).map((job) => (
                <span
                  key={job.value}
                  className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium border border-blue-200 max-w-[200px]">
                  <Buildings size={10} className="flex-shrink-0" />
                  <span className="truncate">{job.label}</span>
                  <button
                    className="flex-shrink-0 flex items-center justify-center rounded-full hover:bg-red-200 p-0.5 cursor-pointer bg-transparent border-none text-red-500"
                    onClick={() =>
                      setSelectedJobIds(
                        selectedJobIds.filter((id) => id !== job.value && id !== 'ALL')
                      )
                    }>
                    <X size={10} />
                  </button>
                </span>
              ))}
              {activeJobs.length > 2 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium border border-gray-200">
                  +{activeJobs.length - 2}
                </span>
              )}
              <button
                className="text-xs text-red-500 hover:text-red-700 font-medium cursor-pointer bg-transparent border-none"
                onClick={() => setSelectedJobIds([])}>
                Clear All
              </button>
            </>
          )}
        </div>
      )}
      <BuyerScheduledPayments />
      <div className="flex-1 min-h-0">
        {sharedModals}
        <DillTable<PaymentRow>
          columns={columns}
          data={paymentRows}
          rowKey={(r) => r.id}
          tableClassName={isMobileWidth ? 'bg-gray-50' : undefined}
          maxHeight={isMobileWidth ? undefined : 'calc(100vh - 320px)'}
          sortable
          sortCycle="3state"
          scrollX
          search
          searchPlaceholder="Search invoice, supplier..."
          virtualize
          rowHeight={70}
          striped={false}
          rowPadding={0}
          toolbarLeft={toolbarLeft}
          showCount={false}
          resizable
          searchClassName="h-9"
          mobileBreakpoint={800}
          renderMobileCard={renderMobileCard}
        />
      </div>
      <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1 min-w-0">
          <BuyerPaymentMethods />
        </div>
        <div className="flex flex-col items-start gap-1.5 flex-1 sm:flex-none sm:pl-4 sm:border-l border-slate-200 h-full">
          <div className="font-semibold text-sm text-gray-900 sm:mb-2">Auto Pay</div>
          <div
            onClick={isAutopaySetupEnabled ? () => setOpenAutopayModal(true) : undefined}
            className={`flex flex-col w-full sm:w-[300px] border rounded-xl overflow-hidden transition-all duration-150 ${
              isAutopaySetupEnabled
                ? 'bg-white hover:shadow-md cursor-pointer'
                : 'bg-slate-50 opacity-75'
            } ${isAutoPayActive ? 'border-green-200' : 'border-slate-200'}`}>
            {/* Main row */}
            <div className="flex items-center gap-3 px-4 py-3.5">
              <div className={`shrink-0 ${isAutoPayActive ? 'text-green-500' : 'text-amber-500'}`}>
                <ArrowClockwise size={26} weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-slate-800 truncate flex items-center gap-2">
                  {selectedSupplierForBuyer?.name ?? 'No supplier selected'}
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                      isAutoPayActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                    {isAutoPayActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {!isAutopaySetupEnabled
                    ? "Reach out to your supplier if you'd like to setup Autopay on your account."
                    : isAutoPaySelected
                    ? 'Automatic payments enabled'
                    : 'Click to configure'}
                </div>
              </div>
              {isAutopaySetupEnabled && <CaretRight size={16} className="text-slate-300" />}
            </div>

            {/* Details row */}
            {isAutoPayActive && (
              <div className="border-t border-slate-100 px-4 py-2.5 flex items-center gap-4">
                {(() => {
                  const pmId = selectedSupplierForBuyer?.buyer?.autopayDetails?.paymentMethodId;
                  const pm = pmId ? paymentMethods.find((m) => m.id === pmId) : null;
                  return (
                    <div className="flex items-center gap-1.5 min-w-0">
                      {pm?.type === 'BANK_ACCOUNT' ? (
                        <Bank size={13} className="text-blue-500 shrink-0" />
                      ) : (
                        <CreditCard size={13} className="text-violet-500 shrink-0" />
                      )}
                      <span className="text-xs text-slate-500 truncate">
                        {pm?.paymentMethodName ?? 'No method set'}
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
